import { Button, Modal, Select, formatCurrency } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  billingApi,
  getApiError,
  type BillingPeriod,
  type Currency,
  type Subscription,
} from '../../lib/api.js';
import { RequestAlert } from '../../app/data-view.js';

const periodLabels: Record<BillingPeriod, string> = {
  MONTHLY: 'Mensal',
  YEARLY: 'Anual',
};

const currencyLabels: Record<Currency, string> = {
  BRL: 'Real',
  USD: 'Dólar',
  ARS: 'Peso argentino',
};

/// Contratar um plano reescreve o que a empresa pode usar. É o único caminho pelo qual alguém perde
/// acesso — por isso a tela mostra antes o que sai, em vez de deixar a pessoa descobrir depois.
export function TenantSubscriptionDialog({
  tenantId,
  tenantDisplayName,
  onClose,
  onApplied,
}: {
  tenantId: string;
  tenantDisplayName: string;
  onClose: () => void;
  onApplied: (subscription: Subscription) => Promise<void>;
}) {
  const [planKey, setPlanKey] = useState('');
  const [slot, setSlot] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const current = useQuery({
    queryKey: ['root', 'subscription', tenantId],
    queryFn: () => billingApi.subscription(tenantId),
    retry: false,
  });
  const plans = useQuery({
    queryKey: ['root', 'plans', { activity: 'ACTIVE' }],
    queryFn: () => billingApi.plans({ activity: ['ACTIVE'] }),
    retry: false,
  });
  const modules = useQuery({
    queryKey: ['root', 'modules'],
    queryFn: billingApi.modules,
    retry: false,
  });

  const chosen = (plans.data ?? []).find((plan) => plan.key === planKey);
  const nothingForSale = plans.isSuccess && plans.data.length === 0;
  const priceOptions = useMemo(
    () => [
      { value: '', label: 'Escolha moeda e periodicidade' },
      ...(chosen?.prices ?? []).map((price) => ({
        value: `${price.currency}:${price.period}`,
        label: `${currencyLabels[price.currency]} · ${periodLabels[price.period]} · ${formatCurrency(
          price.amountCents,
          price.currency,
        )}`,
      })),
    ],
    [chosen],
  );

  const catalog = modules.data ?? [];
  const nameOf = (key: string) =>
    catalog.find((item) => item.key === key)?.displayName ??
    catalog.flatMap((item) => item.features).find((item) => item.key === key)?.displayName ??
    key;

  // O que a empresa perde ao trocar de plano. Mostrar antes é o que separa uma decisão de um susto.
  const losing = (current.data?.moduleKeys ?? []).filter(
    (key) => chosen !== undefined && !chosen.moduleKeys.includes(key),
  );

  async function confirm() {
    setError(undefined);
    if (planKey === '') return setError('Escolha o plano.');
    const [currency, period] = slot.split(':') as [Currency, BillingPeriod];
    if (!slot) return setError('Escolha a moeda e a periodicidade.');

    setBusy(true);
    try {
      await onApplied(await billingApi.applyPlan(tenantId, { planKey, currency, period }));
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Contratação"
      description={`O que ${tenantDisplayName} tem direito de usar.`}
    >
      {current.error ? (
        <RequestAlert message={getApiError(current.error)} />
      ) : (
        <div
          role="group"
          aria-label="Contratação atual"
          className="mb-5 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs"
        >
          {current.isLoading ? (
            // Dizer "nenhum plano contratado" enquanto a resposta não chegou seria mentir para
            // quem está prestes a decidir uma troca.
            <p className="text-content-muted">Carregando a contratação…</p>
          ) : current.data?.planKey ? (
            <>
              <p>
                Plano atual: <strong>{current.data.planDisplayName}</strong> ·{' '}
                <span className="tabular-nums">
                  {formatCurrency(current.data.amountCents ?? 0, current.data.currency ?? 'BRL')}
                </span>{' '}
                {periodLabels[current.data.period ?? 'MONTHLY'].toLowerCase()}
              </p>
              <p className="mt-1 text-content-faint">
                Preço congelado na contratação — mudar o plano no catálogo não altera este valor.
              </p>
            </>
          ) : (
            <p>Nenhum plano contratado. O acesso atual foi concedido fora de um plano.</p>
          )}
          {current.isLoading ? null : (
            <p className="mt-2 text-content-muted">
              Usa hoje:{' '}
              {(current.data?.moduleKeys ?? []).map(nameOf).join(', ') || 'nada além do básico'}
            </p>
          )}
        </div>
      )}

      {plans.error ? <RequestAlert message={getApiError(plans.error)} /> : null}
      {nothingForSale ? (
        // Plano fora de venda ou sem preço não se contrata. Sem este aviso a lista abre vazia e
        // parece defeito, quando o que falta é um passo no catálogo.
        <p
          role="status"
          className="mb-5 rounded-xl border border-warning/40 bg-warning/12 px-4 py-3 text-xs text-warning"
        >
          Nenhum plano à venda. Em Planos, informe ao menos um preço e marque o plano como{' '}
          <strong>À venda</strong> para poder contratá-lo aqui.
        </p>
      ) : null}

      <div className="grid gap-5">
        <Select
          label="Plano"
          value={planKey}
          options={[
            { value: '', label: 'Escolha o plano' },
            ...(plans.data ?? []).map((plan) => ({ value: plan.key, label: plan.displayName })),
          ]}
          onValueChange={(value) => {
            setPlanKey(value);
            setSlot('');
          }}
        />
        {chosen ? (
          <Select
            label="Moeda e periodicidade"
            value={slot}
            options={priceOptions}
            onValueChange={setSlot}
          />
        ) : null}
      </div>

      {losing.length > 0 ? (
        <p
          role="status"
          className="mt-5 rounded-xl border border-warning/40 bg-warning/12 px-4 py-3 text-xs text-warning"
        >
          Ao trocar, a empresa perde acesso a: <strong>{losing.map(nameOf).join(', ')}</strong>. Os
          dados continuam guardados e voltam se o acesso for devolvido.
        </p>
      ) : null}

      {error ? <RequestAlert message={error} /> : null}

      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          disabled={nothingForSale}
          onClick={() => void confirm()}
        >
          Aplicar plano
        </Button>
      </div>
    </Modal>
  );
}
